/**
 * 큰 수 축약 한 벌 — 호출처마다 다른 표기를 옵션으로 가른다.
 *
 * 표기가 갈리는 축은 일곱이다(어느 표기가 정본인가는 A383 판단 대기).
 * ```
 *   ① 수로 못 읽을 때      '-'  ·  '0'
 *   ② null·undefined      0 으로 보나(Number(v ?? 0))  ·  그대로 Number(v)
 *   ③ 0 이하              '-'  ·  그대로  ·  0 으로 깎기
 *   ④ 소수 자리            1  ·  작은 단위만 0(슬라이더)  ·  `.0` 떼기
 *   ⑤ 축약 안 하는 구간     toString  ·  ko-KR 천단위  ·  fNumber(앱 로케일)
 *   ⑥ ±Infinity          막는다  ·  **안 막는다**(format-utils 만 — `Infinity` → `'InfinityM'`)
 *   ⑦ 단위 체계            `K·M`(천·백만)  ·  `천·만·억`(한국 자릿수)
 * ```
 *
 * ⑦ 은 **눈금이 다르다**. 한국어 화면은 만·억으로 끊어 읽어서 `1.2M` 이 「백이십만」으로 바로
 * 안 읽힌다. 두 체계는 자릿수가 안 맞으므로(만 = 10⁴ · M = 10⁶) 한 화면에서 섞지 않는다.
 *
 * ⑥ 은 실데이터로는 닿지 않는 갈래다(JSON 이 Infinity 를 못 싣는다). 그래도 옵션으로 남긴다 —
 * 「닿지 않으니 바꿔도 된다」가 아니다.
 *
 * ⚠️ **여기가 표기를 통일하는 자리가 아니다.** 각 호출처가 옵션 조합으로 자기 표기를 낸다.
 * 통일하려면 여기 기본값을 바꾸는 것이 아니라 **정본 표기를 먼저 정한 뒤** 호출처의 옵션을
 * 걷어내라 — 그러라고 옵션으로 갈라 뒀다.
 *
 * ⚠️ ⑤의 `'locale'`(ko-KR 고정)과 `'fNumber'`(앱 로케일)는 로케일이 바뀌면 갈린다.
 * 같아 보인다고 합치지 마라 — 화면 문자열이 바뀌는 자리다.
 *
 * 자리별 조합은 `__tests__/format-compact-number.test.ts` 가 경계값으로 못 박는다.
 */

import { fNumber } from 'src/utils/format/format-number';

// ----------------------------------------------------------------------

/** 1000 미만을 어떻게 적을 것인가 */
export type CompactBelowStyle =
  /** `String(n)` — 천단위 구분이 **없다** */
  | 'plain'
  /** `toLocaleString('ko-KR')` — 천단위 구분 */
  | 'locale'
  /** `fNumber` — 앱 로케일을 따른다 */
  | 'fNumber';

/** 「수로 못 읽었다」의 기준 */
export type CompactInvalidWhen =
  /** 유한수가 아니면 전부 — `NaN`·`±Infinity` */
  | 'nonFinite'
  /** `NaN` 일 때만 — `±Infinity` 는 그대로 계산에 들어간다(`'InfinityM'`) */
  | 'nan';

/** 0 이하를 어떻게 할 것인가 */
export type CompactNonPositive =
  /** `fallback` 문자열로 (팔로워 수 계열) */
  | 'dash'
  /** 그대로 적는다(음수도 그대로) */
  | 'keep'
  /** 0 으로 깎는다 */
  | 'zero';

/** 어느 자릿수로 끊어 읽을 것인가 */
export type CompactUnitSystem =
  /** `1.2K`·`3.4M` — 천·백만 */
  | 'latin'
  /** `1.2천`·`3.4만`·`1.5억` — 한국 자릿수 */
  | 'ko';

export type CompactNumberOptions = {
  /** 수로 못 읽었을 때 낼 문자열. 기본 `'-'` */
  fallback?: string;
  /** 「못 읽었다」의 기준. 기본 `'nonFinite'` */
  invalidWhen?: CompactInvalidWhen;
  /** `null`·`undefined` 를 0 으로 볼 것인가(`Number(v ?? 0)`). 기본 `true` */
  nullishAsZero?: boolean;
  /** 0 이하 처리. 기본 `'dash'` */
  nonPositive?: CompactNonPositive;
  /** 소수 자리. 수 하나면 모든 단위, 객체면 작은 단위(`k`)·큰 단위(`m`)별. 기본 1 */
  decimals?: number | { k?: number; m?: number };
  /** `1.0K`·`1.0만` 의 `.0` 을 뗄 것인가. 기본 `false` */
  trimPointZero?: boolean;
  /** 축약 구간에 못 미치는 수의 표기. 기본 `'locale'` */
  below?: CompactBelowStyle;
  /** 단위 체계. 기본 `'latin'` */
  unit?: CompactUnitSystem;
  /** 이 값 미만은 축약하지 않고 `below` 로 적는다. 기본은 단위 체계의 가장 작은 단위(1000) */
  compactFrom?: number;
};

/**
 * 한국어 화면의 개수·합계 카드 표기 — `5,432` · `41.9만` · `54.5억`.
 * 천 단위는 쓰지 않는다(만 미만은 ko-KR 천단위). 0 이하·못 읽음은 `'-'`.
 */
export const KO_COUNT_COMPACT: CompactNumberOptions = {
  unit: 'ko',
  trimPointZero: true,
  compactFrom: 10_000,
};

const K = 1_000;
const M = 1_000_000;
const MAN = 10_000;
const EOK = 100_000_000;

function formatBelow(n: number, style: CompactBelowStyle): string {
  if (style === 'plain') return n.toString();
  if (style === 'fNumber') return fNumber(n);
  return n.toLocaleString('ko-KR');
}

function unitLabel(n: number, unit: number, suffix: string, digits: number, trim: boolean): string {
  const text = `${(n / unit).toFixed(digits)}${suffix}`;
  return trim ? text.replace(`.0${suffix}`, suffix) : text;
}

/**
 * 큰 수를 `1.2K`·`3.4M`(기본) 또는 `1.2천`·`3.4만`·`1.5억`(`unit: 'ko'`)로 줄인다.
 *
 * 기본값은 가장 흔한 꼴이다 — 못 읽으면 `'-'` · null 은 0 으로 · 0 이하는 `'-'` ·
 * 소수 한 자리 · `.0` 유지 · 축약 구간 아래는 ko-KR 천단위.
 */
export function formatCompactNumber(value: unknown, options: CompactNumberOptions = {}): string {
  const {
    fallback = '-',
    invalidWhen = 'nonFinite',
    nullishAsZero = true,
    nonPositive = 'dash',
    decimals = 1,
    trimPointZero = false,
    below = 'locale',
    unit = 'latin',
    compactFrom = K,
  } = options;

  const raw = nullishAsZero ? Number(value ?? 0) : Number(value);
  const invalid = invalidWhen === 'nan' ? Number.isNaN(raw) : !Number.isFinite(raw);
  if (invalid) return fallback;

  let n = raw;
  if (n <= 0) {
    if (nonPositive === 'dash') return fallback;
    if (nonPositive === 'zero') n = 0;
  }

  const kDigits = typeof decimals === 'number' ? decimals : decimals.k ?? 1;
  const mDigits = typeof decimals === 'number' ? decimals : decimals.m ?? 1;

  if (n < compactFrom) return formatBelow(n, below);

  if (unit === 'ko') {
    if (n >= EOK) return unitLabel(n, EOK, '억', mDigits, trimPointZero);
    if (n >= MAN) return unitLabel(n, MAN, '만', kDigits, trimPointZero);
    if (n >= K) return unitLabel(n, K, '천', kDigits, trimPointZero);
    return formatBelow(n, below);
  }

  if (n >= M) return unitLabel(n, M, 'M', mDigits, trimPointZero);
  if (n >= K) return unitLabel(n, K, 'K', kDigits, trimPointZero);
  return formatBelow(n, below);
}
